import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, ArrowLeft, KeyRound, Lock, Eye, EyeOff, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('email'); // 'email', 'code', 'success'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = t?.forgotPassword?.emailRequired || 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t?.forgotPassword?.invalidEmail || 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCode = () => {
    const newErrors = {};
    if (!formData.code.trim()) {
      newErrors.code = t?.forgotPassword?.codeRequired || 'Verification code is required';
    } else if (formData.code.length !== 8 || !/^\d+$/.test(formData.code)) {
      newErrors.code = t?.forgotPassword?.invalidCode || 'Please enter a valid 8-digit code';
    }
    if (!formData.newPassword) {
      newErrors.newPassword = t?.forgotPassword?.passwordRequired || 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t?.forgotPassword?.passwordMinLength || 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = t?.forgotPassword?.passwordUppercase || 'Password must contain an uppercase letter';
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = t?.forgotPassword?.passwordLowercase || 'Password must contain a lowercase letter';
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = t?.forgotPassword?.passwordNumber || 'Password must contain a number';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t?.forgotPassword?.passwordMismatch || 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: formData.email
      });
      toast.success(t?.forgotPassword?.codeSent || 'If an account exists, a reset code has been sent to your email');
      setStep('code');
    } catch (err) {
      toast.error(t?.forgotPassword?.errorSending || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validateCode()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: formData.email,
        code: formData.code,
        new_password: formData.newPassword
      });
      toast.success(t?.forgotPassword?.resetSuccess || 'Password reset successfully!');
      setStep('success');
    } catch (err) {
      // WO-03 item 3 — reset-password can be rate-limited; show the wait, don't
      // treat it as a bad code.
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers?.['retry-after'];
        const wait = retryAfter
          ? ` Please wait ${retryAfter} seconds before trying again.`
          : ' Please wait a moment before trying again.';
        toast.error(`Too many attempts.${wait}`);
        return;
      }
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to reset password';
      if (message.includes('Invalid reset code')) {
        setErrors({ code: t?.forgotPassword?.invalidCode || 'Invalid verification code' });
      } else if (message.includes('expired')) {
        setErrors({ code: t?.forgotPassword?.codeExpired || 'Code expired. Please request a new one.' });
      } else if (message.includes('Too many')) {
        setErrors({ code: t?.forgotPassword?.tooManyAttempts || 'Too many attempts. Please request a new code.' });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const renderEmailStep = () => (
    <form onSubmit={handleRequestReset}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className={isRTL ? 'text-right block' : ''}>
            {t?.forgotPassword?.emailLabel || 'Email Address'}
          </Label>
          <div className="relative">
            <Mail className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
            <Input
              id="email"
              type="email"
              placeholder={t?.forgotPassword?.emailPlaceholder || 'Enter your email'}
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={`${isRTL ? 'pr-10' : 'pl-10'} h-12`}
              dir="ltr"
              data-testid="forgot-email-input"
            />
          </div>
          {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
        </div>

        <Button 
          type="submit" 
          className="w-full h-12 text-lg font-semibold mt-6"
          disabled={loading}
          data-testid="send-code-button"
        >
          {loading 
            ? (t?.forgotPassword?.sending || 'Sending...') 
            : (t?.forgotPassword?.sendCode || 'Send Reset Code')}
        </Button>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <form onSubmit={handleResetPassword}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground text-center mb-4">
          {t?.forgotPassword?.codeSentTo || 'We sent an 8-digit code to'} <strong>{formData.email}</strong>
        </p>

        {/* Verification Code */}
        <div className="space-y-2">
          <Label htmlFor="code" className={isRTL ? 'text-right block' : ''}>
            {t?.forgotPassword?.codeLabel || 'Verification Code'}
          </Label>
          <div className="relative">
            <KeyRound className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
            <Input
              id="code"
              type="text"
              placeholder="00000000"
              value={formData.code}
              onChange={(e) => updateField('code', e.target.value.replace(/\D/g, '').slice(0, 8))}
              className={`${isRTL ? 'pr-10' : 'pl-10'} h-12 text-center text-2xl tracking-[0.4em] font-mono`}
              dir="ltr"
              maxLength={8}
              data-testid="reset-code-input"
            />
          </div>
          {errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label htmlFor="newPassword" className={isRTL ? 'text-right block' : ''}>
            {t?.forgotPassword?.newPasswordLabel || 'New Password'}
          </Label>
          <div className="relative">
            <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
            <Input
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder={t?.forgotPassword?.newPasswordPlaceholder || 'Enter new password'}
              value={formData.newPassword}
              onChange={(e) => updateField('newPassword', e.target.value)}
              className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12`}
              dir="ltr"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground`}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.newPassword && <p className="text-sm text-destructive">{errors.newPassword}</p>}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className={isRTL ? 'text-right block' : ''}>
            {t?.forgotPassword?.confirmPasswordLabel || 'Confirm Password'}
          </Label>
          <div className="relative">
            <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t?.forgotPassword?.confirmPasswordPlaceholder || 'Confirm new password'}
              value={formData.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12`}
              dir="ltr"
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} text-muted-foreground hover:text-foreground`}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
        </div>

        <Button 
          type="submit" 
          className="w-full h-12 text-lg font-semibold mt-6"
          disabled={loading}
          data-testid="reset-password-button"
        >
          {loading 
            ? (t?.forgotPassword?.resetting || 'Resetting...') 
            : (t?.forgotPassword?.resetPassword || 'Reset Password')}
        </Button>

        <button
          type="button"
          onClick={() => setStep('email')}
          className="w-full text-sm text-muted-foreground hover:text-foreground mt-2"
        >
          {t?.forgotPassword?.resendCode || 'Didn\'t receive the code? Request again'}
        </button>
      </div>
    </form>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        <Check className="w-10 h-10 text-green-600 dark:text-green-400" />
      </div>
      <div>
        <h3 className="text-xl font-semibold mb-2">
          {t?.forgotPassword?.successTitle || 'Password Reset Successfully!'}
        </h3>
        <p className="text-muted-foreground">
          {t?.forgotPassword?.successMessage || 'You can now log in with your new password.'}
        </p>
      </div>
      <Button 
        onClick={() => navigate('/login')}
        className="w-full h-12 text-lg font-semibold"
        data-testid="go-to-login-button"
      >
        {t?.forgotPassword?.goToLogin || 'Go to Login'}
      </Button>
    </div>
  );

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/login">
            <img src="/logo.png" alt="Mid2Bid" className="h-12 mx-auto" />
          </Link>
        </div>

        <Card className="border-2 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{fontFamily: "'Sora', sans-serif"}}>
              {step === 'success' 
                ? (t?.forgotPassword?.successTitle || 'Success!') 
                : (t?.forgotPassword?.title || 'Reset Password')}
            </CardTitle>
            {step !== 'success' && (
              <CardDescription>
                {step === 'email' 
                  ? (t?.forgotPassword?.subtitle || 'Enter your email to receive a reset code')
                  : (t?.forgotPassword?.enterCodeSubtitle || 'Enter the code and your new password')}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent>
            {step === 'email' && renderEmailStep()}
            {step === 'code' && renderCodeStep()}
            {step === 'success' && renderSuccessStep()}

            {step !== 'success' && (
              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className={`text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t?.forgotPassword?.backToLogin || 'Back to Login'}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
